import { Controller } from 'stimulus'
import $ from 'jquery'
import { getCsrfParamsAsObject } from '~/common/helpers/csrf'

const RECORD_VIEW_FLUSH_INTERVAL_MS = 10000

export default class extends Controller {
  static values = { recordViewUrl: String }

  connect() {
    // --visible-top-bar-height is (currently) only consumed by the edge-to-edge right sidebar.
    if (document.querySelector('.layout-right-sidebar-edge-to-edge')) {
      this.scheduleVisibleTopBarHeight()
      if (import.meta.env.DEV) {
        window.addEventListener('load', this.scheduleVisibleTopBarHeight)
      }
      window.addEventListener('scroll', this.scheduleVisibleTopBarHeight, {
        passive: true,
      })
    }

    this.pendingViewables = []
    this.recordViewFlushIntervalId = setInterval(
      this.flushRecordViewQueue,
      RECORD_VIEW_FLUSH_INTERVAL_MS
    )
    document.addEventListener(
      'visibilitychange',
      this.handleRecordViewVisibilityChange
    )
    window.addEventListener('pagehide', this.flushRecordViewQueueWithBeacon)
    window.addEventListener('message', this.handleWorksheetCompleted)
  }

  disconnect() {
    window.removeEventListener('scroll', this.scheduleVisibleTopBarHeight)
    if (this._rafId) cancelAnimationFrame(this._rafId)

    clearInterval(this.recordViewFlushIntervalId)
    document.removeEventListener(
      'visibilitychange',
      this.handleRecordViewVisibilityChange
    )
    window.removeEventListener('pagehide', this.flushRecordViewQueueWithBeacon)
    window.removeEventListener('message', this.handleWorksheetCompleted)
    this.flushRecordViewQueueWithBeacon()
  }

  handleWorksheetCompleted = (e) => {
    if (e.data?.type !== 'worksheet:completed') return

    const iframes = document.querySelectorAll('iframe.js-listen-for-completion')
    const fromLessonWorksheet = Array.from(iframes).some((iframe) => {
      if (iframe.contentWindow && iframe.contentWindow === e.source) return true

      // A Turbo-restored page may have a new iframe WindowProxy while a
      // submission from the cached frame is still completing.
      try {
        return new URL(iframe.src, document.baseURI).origin === e.origin
      } catch {
        return false
      }
    })
    if (fromLessonWorksheet) window.location.reload()
  }

  scheduleVisibleTopBarHeight = () => {
    if (this._rafId) return
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null
      this.updateVisibleTopBarHeight()
    })
  }

  recordView(e) {
    this.pendingViewables.push({ type: e.detail.type, id: e.detail.id })
  }

  handleRecordViewVisibilityChange = () => {
    if (document.visibilityState === 'hidden')
      this.flushRecordViewQueueWithBeacon()
  }

  flushRecordViewQueue = () => {
    const viewables = this.pendingViewables.splice(
      0,
      this.pendingViewables.length
    )
    if (viewables.length === 0) return

    $.ajax({
      url: this.recordViewUrlValue,
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({ viewables }),
    })
  }

  flushRecordViewQueueWithBeacon = () => {
    const viewables = this.pendingViewables.splice(
      0,
      this.pendingViewables.length
    )
    if (viewables.length === 0) return

    const blob = new Blob(
      [JSON.stringify({ viewables, ...getCsrfParamsAsObject() })],
      { type: 'application/json' }
    )
    navigator.sendBeacon(this.recordViewUrlValue, blob)
  }

  updateVisibleTopBarHeight = () => {
    const header = document.querySelector('.builder-header')
    if (!header) return

    const rect = header.getBoundingClientRect()
    const visibleTop = Math.max(rect.top, 0)
    const visibleBottom = Math.min(rect.bottom, window.innerHeight)
    const height = Math.max(visibleBottom - visibleTop, 0)

    if (height !== this._lastVisibleTopBarHeight) {
      this._lastVisibleTopBarHeight = height
      this.element.style.setProperty('--visible-top-bar-height', `${height}px`)
    }
  }
}
